import React, { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Loader2, RefreshCw, Mail, Phone, Building2, Tag, MessageSquare, Inbox } from "lucide-react";
import { Button } from "../components/ui/button";
import { fetchLeads, fetchLeadStats } from "../lib/api";

const TYPE_LABELS = {
  all: "All Leads",
  demo: "Demo Requests",
  contact: "Contact Messages",
  newsletter: "Newsletter Subscribers",
};

const TYPE_BADGE = {
  demo: "bg-[#F57C20]/15 text-[#D86810] border-[#F57C20]/30",
  contact: "bg-[#15348C]/10 text-[#15348C] border-[#15348C]/20",
  newsletter: "bg-[#6BBE32]/15 text-[#4F9622] border-[#6BBE32]/30",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const AdminLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ demo: 0, contact: 0, newsletter: 0, total: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter === "all" ? {} : { type: filter };
      const [list, s] = await Promise.all([fetchLeads(params), fetchLeadStats()]);
      setLeads(list);
      setStats(s);
    } catch (err) {
      setError(err?.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white min-h-screen">
      <Header />

      <section className="max-w-7xl mx-auto px-4 pt-28 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-widest text-[#F57C20]">ADMIN · LEAD INBOX</p>
            <h1 className="text-[#15348C] mt-1" style={{ fontSize: "2.25rem" }}>Captured Leads</h1>
            <p className="text-gray-600 mt-1 text-sm">Live feed of demo requests, contact messages and newsletter subscriptions.</p>
          </div>
          <Button onClick={load} disabled={loading} data-testid="leads-refresh"
            className="bg-[#15348C] hover:bg-[#0A1F5C] text-white h-10 rounded-md">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin"/> : <RefreshCw size={16} className="mr-2"/>}
            Refresh
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="leads-stats">
          <StatCard label="Total Leads" value={stats.total} accent="#15348C"/>
          <StatCard label="Demo Requests" value={stats.demo} accent="#F57C20"/>
          <StatCard label="Contact Messages" value={stats.contact} accent="#15348C"/>
          <StatCard label="Newsletter Subscribers" value={stats.newsletter} accent="#6BBE32"/>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="leads-filters">
          {Object.keys(TYPE_LABELS).map((k) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${
                filter === k
                  ? "bg-[#15348C] text-white border-[#15348C]"
                  : "bg-white text-[#15348C] border-gray-200 hover:border-[#15348C]"
              }`}>
              {TYPE_LABELS[k]}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading && (
            <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
              <Loader2 size={18} className="animate-spin"/> Loading leads…
            </div>
          )}
          {!loading && error && (
            <div data-testid="leads-error" className="border border-red-200 bg-red-50 text-red-700 rounded-lg p-4 text-sm">
              {error}
            </div>
          )}
          {!loading && !error && leads.length === 0 && (
            <div data-testid="leads-empty" className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-2xl">
              <Inbox size={32} className="mx-auto mb-3 text-gray-400"/>
              No leads yet for this filter.
            </div>
          )}
          {!loading && !error && leads.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="leads-list">
              {leads.map((lead) => (
                <article key={lead.id} data-testid={`lead-card-${lead.type}`}
                  className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-md border ${TYPE_BADGE[lead.type] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                      {lead.type}
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(lead.created_at)}</span>
                  </div>
                  <h3 className="text-[#15348C] mt-3" style={{ fontSize: "1.1rem" }}>
                    {lead.name || lead.email}
                  </h3>
                  <div className="mt-3 space-y-1.5 text-sm text-gray-700">
                    <p className="flex items-center gap-2"><Mail size={14} className="text-[#6BBE32]"/> {lead.email}</p>
                    {lead.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-[#6BBE32]"/> {lead.phone}</p>}
                    {lead.company && <p className="flex items-center gap-2"><Building2 size={14} className="text-[#6BBE32]"/> {lead.company}</p>}
                    {lead.interest && <p className="flex items-center gap-2"><Tag size={14} className="text-[#6BBE32]"/> {lead.interest}</p>}
                    {lead.message && (
                      <p className="flex gap-2 text-gray-600 italic mt-2">
                        <MessageSquare size={14} className="text-[#6BBE32] mt-0.5 shrink-0"/>
                        <span className="leading-snug">“{lead.message}”</span>
                      </p>
                    )}
                    {lead.source && (
                      <p className="text-xs text-gray-400 mt-2">via <span className="font-semibold">{lead.source}</span></p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
    <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase">{label}</p>
    <p className="text-3xl font-bold mt-2" style={{ color: accent, fontFamily: "Times New Roman, serif" }}>{value}</p>
  </div>
);

export default AdminLeadsPage;
